import React, { useState } from 'react';
import { Send, Bot, AlertCircle, Loader2 } from 'lucide-react';

const App = () => {
  const [problem, setProblem] = useState('');
  const [advice, setAdvice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const getOpenAIAdvice = async (problem) => {
    const response = await fetch('/api/fix-my-mess', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ problem })
    });
  
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (parseError) {
        errorMessage = `Server error: ${response.status}`;
      }
      throw new Error(errorMessage);
    }
  
    const data = await response.json();
    return data.advice;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setAdvice('');

    if (!problem.trim()) {
      setError('Please describe your problem first!');
      return;
    }

    setIsLoading(true);

    try {
      const aiAdvice = await getOpenAIAdvice(problem);
      setAdvice(aiAdvice);
    } catch (err) {
      console.error('API error:', err);
      
      // Friendly error messages for users
      let userMessage = 'Something went wrong. Please try again.';
      
      if (err.message.includes('Failed to fetch')) {
        userMessage = 'Connection error. Please check your internet and try again.';
      } else if (err.message.includes('OpenAI API error')) {
        userMessage = 'AI service temporarily unavailable. Please try again in a moment.';
      } else if (err.message.includes('Failed to get advice')) {
        userMessage = 'Unable to generate advice right now. Please try again.';
      }
      
      setError(userMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatAdvice = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br>');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">

        {/* Header with Star Burst Lights and Mission */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 sm:gap-6 lg:gap-8 mb-6">
            
            {/* Left Star Lights */}
            <div className="relative">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-yellow-400 rounded-full animate-ping" style={{animationDuration: '2s'}}></div>
              <div className="absolute top-0 left-0 w-3 h-3 sm:w-4 sm:h-4 bg-yellow-300 rounded-full animate-pulse" style={{animationDelay: '0.5s', animationDuration: '2s'}}></div>
            </div>
            <div className="relative">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-yellow-400 rounded-full animate-ping" style={{animationDelay: '0.3s', animationDuration: '2s'}}></div>
              <div className="absolute top-0 left-0 w-2 h-2 sm:w-3 sm:h-3 bg-yellow-300 rounded-full animate-pulse" style={{animationDelay: '0.8s', animationDuration: '2s'}}></div>
            </div>

            {/* Main Title */}
            <div className="flex items-center gap-3">
              <img src="/fmm-logo.png" alt="FMM Logo" className="w-8 h-8 sm:w-12 sm:h-12" />
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 whitespace-nowrap">Fix My Mess</h1>
            </div>

            {/* Right Star Lights */}
            <div className="relative">
              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-yellow-400 rounded-full animate-ping" style={{animationDelay: '0.6s', animationDuration: '2s'}}></div>
              <div className="absolute top-0 left-0 w-2 h-2 sm:w-3 sm:h-3 bg-yellow-300 rounded-full animate-pulse" style={{animationDelay: '1.1s', animationDuration: '2s'}}></div>
            </div>
            <div className="relative">
              <div className="w-3 h-3 sm:w-4 sm:h-4 bg-yellow-400 rounded-full animate-ping" style={{animationDelay: '0.9s', animationDuration: '2s'}}></div>
              <div className="absolute top-0 left-0 w-3 h-3 sm:w-4 sm:h-4 bg-yellow-300 rounded-full animate-pulse" style={{animationDelay: '1.4s', animationDuration: '2s'}}></div>
            </div>
          </div>
          
          <p className="text-lg text-teal-700 font-medium leading-relaxed">
            Anonymous, compassionate, judgment-free AI guidance for any life situation
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Problem Input */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe your problem or situation:
              </label>
              <textarea
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                placeholder="Tell me about your mess in detail... The more context you provide, the better advice I can give.

Examples:
• I accidentally sent a critical email to the wrong person...
• My best friend is dating my ex and I don't know how to handle it...
• I lied on my resume and got the job, now I'm panicking...
• I spent money I don't have and my partner doesn't know..."
                className="w-full h-40 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
            </div>
            {/* AI Disclaimer and Donation Info */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertCircle className="w-4 h-4" />
                <span><strong>AI can make mistakes</strong> • Double-check important info 💝</span>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={isLoading || !problem.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-4 px-6 rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  AI is analyzing your situation...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Fix My Mess!
                </>
              )}
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-red-800 mb-2">Oops!</h3>
                  <p className="text-sm text-red-700">
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Advice Display */}
          {advice && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <Bot className="w-6 h-6 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-900 mb-3 text-lg">
                    🤖 AI-Powered Life Advice:
                  </h3>
                  <div 
                    className="text-gray-800 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: formatAdvice(advice) }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Donation Section - Shows after advice */}
          {advice && (
            <div className="mt-6 text-center p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm mb-3">
                Did this advice help? Support us to keep this service free for everyone!
              </p>
              <a
                href="https://paystack.shop/pay/support-fix-my-mess"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors"
              >
                💝 Donate
              </a>
            </div>
          )}

          {/* Usage Stats */}
          {advice && (
            <div className="mt-4 text-center">
              <button
                onClick={() => {
                  setProblem('');
                  setAdvice('');
                  setError('');
                }}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Ask about another problem →
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="mt-12 bg-gray-50 rounded-2xl p-6 border border-gray-200">
          <div className="max-w-2xl mx-auto text-center space-y-4">
            
            <div className="flex items-center justify-center gap-2 text-teal-600 mb-3">
              <Bot className="w-4 h-4" />
              <span className="font-medium text-sm">Important</span>
            </div>
            
            <p className="text-sm text-gray-700">
              <strong>Not a substitute for professional mental health care.</strong><br/>
              🚨 For emergencies, contact a professional counselor immediately.
            </p>

            <div className="flex justify-center gap-4 text-xs text-gray-500 pt-2 border-t border-gray-200">
              <span>© 2025 Fix My Mess</span>
              <a href="#" className="text-teal-600 hover:text-teal-800">Privacy</a>
              <a href="#" className="text-teal-600 hover:text-teal-800">Contact</a>
            </div>
          </div>
        </footer> 
      </div>
    </div>
  );
};

export default App;
